import { defineStore } from 'pinia'
import labels from '@/translations/labels.json'
import CONSTANTS from '@/constants/Config.constants'
import { useProjectStore } from './projectStore'
import { useSnapshotStore } from './snapshotStore'
import { useDashboardStore } from './dashboardStore'
import { RouteLocationRaw } from 'vue-router'
import { useGlobalFilterStore } from './globalFilterStore'
import { SnaphotReference } from '@/models'

export interface SidebarState {
  snapshotID: SnaphotReference
  railState: boolean
}

export interface SimpleItem {
  type: 'simpleitem' | 'configurationItem'
  to: RouteLocationRaw
  id: string
  title: string
  icon?: string
  featureId?: string
  disabled?: boolean
}

export interface DashboardItem {
  type: 'dashboarditem'
  dashboards: SimpleItem[]
  featureId?: string
  disabled?: boolean
}

export type SidebarItem = SimpleItem | DashboardItem

function checkIfCurrentProjectIsUndefined(): boolean {
  return useProjectStore().currentProject === undefined
}

function checkIfCurrentSnapshotIsUndefined(): boolean {
  return useSnapshotStore().currentSnapshot === undefined
}

/*function generateProjectOverviewLink(): RouteLocationRaw {
  const projectId = useProjectStore().currentProject?.id.toString()
  const filter = useGlobalFilterStore().selectedGlobalFilterId

  return {
    name: labels.SIDEBAR.MAIN.PROJECT_OVERVIEW,
    params: { projectId },
    query: { filter },
  }
}
*/

function generateProjectConfigurationLink(): RouteLocationRaw {
  const projectId = useProjectStore().currentProject?.id.toString()
  const filter = useGlobalFilterStore().selectedGlobalFilterId

  return {
    name: labels.SIDEBAR.MAIN.PROJECT_CONFIGURATION,
    params: { projectId },
    query: { filter },
  }
}

function generateSnapshotPageLink(): RouteLocationRaw {
  const projectId = useProjectStore().currentProject?.id.toString()
  const filter = useGlobalFilterStore().selectedGlobalFilterId

  return {
    name: labels.SIDEBAR.MAIN.SNAPSHOTS_MANAGMENT,
    params: { projectId },
    query: { filter },
  }
}

function generateGlobalFilterPageLink(): RouteLocationRaw {
  const projectId = useProjectStore().currentProject?.id.toString()
  const filter = useGlobalFilterStore().selectedGlobalFilterId

  return {
    name: labels.SIDEBAR.MAIN.GLOBAL_FILTERS,
    params: { projectId },
    query: { filter },
  }
}

function generateCustomMetricsPageLink(): RouteLocationRaw {
  const projectId = useProjectStore().currentProject?.id.toString()
  const filter = useGlobalFilterStore().selectedGlobalFilterId

  return {
    name: labels.CUSTOM_METRIC.CUSTOM_METRICS,
    params: { projectId },
    query: { filter },
  }
}

export function generateDashboardLink(dashboardId: number): RouteLocationRaw {
  const projectId = useProjectStore().currentProject?.id
  const snapshotId = useSnapshotStore().currentSnapshot?.id
  const filter = useGlobalFilterStore().selectedGlobalFilterId

  if (!projectId || !snapshotId) {
    return '#'
  }

  return {
    name: labels.SIDEBAR.MAIN.DASHBOARD,
    params: { projectId, snapshotId, dashboardId },
    query: { filter },
  }
}

function generateExplorerLink(): RouteLocationRaw {
  const projectId = useProjectStore().currentProject?.id
  const snapshotId = useSnapshotStore().currentSnapshot?.id
  const filter = useGlobalFilterStore().selectedGlobalFilterId

  if (!projectId || !snapshotId) {
    return '#'
  }

  return {
    name: labels.SIDEBAR.MAIN.EXPLORER,
    params: { projectId, snapshotId },
    query: { filter },
  }
}

export const useSidebarStore = defineStore('sidebarStore', {
  state: (): SidebarState => ({
    snapshotID: -1,
    railState: false,
  }),
  getters: {
    getHeader(): SimpleItem {
      return {
        type: 'simpleitem',
        to: '#',
        id: 'settings_sidebar_menu',
        title: labels.SIDEBAR.MAIN.TRACEABILITY,
      }
    },
    getItems(): SidebarItem[] {
      const sidebarItems: SidebarItem[] = [
        {
          type: 'simpleitem',
          to: generateDashboardLink(0),
          title: labels.DASHBOARD.DASHBOARDS,
          icon: CONSTANTS.ICONS_MDI.ICON_CHART_BAR,
          id: labels.SIDEBAR.MAIN.DASHBOARDS_ID,
          featureId: 'analyze:dashboard',
          disabled: checkIfCurrentProjectIsUndefined() || checkIfCurrentSnapshotIsUndefined(),
        },
        {
          type: 'dashboarditem',
          dashboards: this.getDashboardItems,
          featureId: 'analyze:dashboard',
        },
        {
          type: 'simpleitem',
          to: generateExplorerLink(),
          title: labels.SIDEBAR.MAIN.EXPLORER,
          icon: 'mdi-selection-search',
          id: labels.SIDEBAR.MAIN.EXPLORER_ID,
          featureId: 'analyze:explorer',
          disabled: checkIfCurrentProjectIsUndefined() || checkIfCurrentSnapshotIsUndefined(),
        },
        {
          type: 'configurationItem',
          to: generateProjectConfigurationLink(),
          title: labels.SIDEBAR.MAIN.PROJECT_CONFIGURATION,
          icon: 'mdi-cog',
          id: labels.SIDEBAR.MAIN.PROJECT_CONFIGURATION,
          disabled: checkIfCurrentProjectIsUndefined(),
        },
      ]
      return sidebarItems.filter((item) => useProjectStore().isFeatureActive(item.featureId))
    },
    getDashboardItems(): SimpleItem[] {
      const dashboardStore = useDashboardStore()

      const dashboardItems: SimpleItem[] = dashboardStore.dashboardStates.map((dashboardState) => ({
        type: 'simpleitem',
        to: generateDashboardLink(dashboardState.dashboard.id),
        title: dashboardState.dashboard.name,
        id: dashboardState.dashboard.id.toString(),
        icon: CONSTANTS.ICONS_MDI.ICON_CHART_BAR,
        disabled: checkIfCurrentProjectIsUndefined() || checkIfCurrentProjectIsUndefined(),
      }))

      return dashboardItems
    },
    getConfigurationItems(): SimpleItem[] {
      const all: SimpleItem[] = [
        {
          type: 'configurationItem',
          to: generateSnapshotPageLink(),
          title: labels.SIDEBAR.MAIN.SNAPSHOTS_MANAGMENT,
          icon: 'mdi-information-outline',
          id: labels.SIDEBAR.MAIN.SNAPSHOTS_MANAGMENT,
          disabled: checkIfCurrentProjectIsUndefined(),
        },
        {
          type: 'configurationItem',
          to: generateGlobalFilterPageLink(),
          title: labels.SIDEBAR.MAIN.GLOBAL_FILTERS,
          icon: 'mdi-information-outline',
          id: labels.SIDEBAR.MAIN.GLOBAL_FILTERS,
          disabled: checkIfCurrentProjectIsUndefined(),
        },
        {
          type: 'configurationItem',
          to: generateCustomMetricsPageLink(),
          title: labels.CUSTOM_METRIC.CUSTOM_METRICS,
          icon: 'mdi-information-outline',
          id: labels.CUSTOM_METRIC.CUSTOM_METRICS,
          featureId: 'analyze:custom-metrics',
          disabled: checkIfCurrentProjectIsUndefined(),
        },
      ]

      return all.filter((item) => useProjectStore().isFeatureActive(item.featureId))
    },
  },
  actions: {
    toggleRailState(): void {
      this.railState = !this.railState
    },
  },
})
